import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ICommonListPayloadDto,
  IRequestRes,
  IRequestResponse,
  IRequestReq,
} from "../type/type.ts";
import axios from "axios";
import { RootState } from "../store/store.ts";
import Axios from "../axios-config/axiosInstance.ts";

interface RequestState {
  requests: IRequestRes[];
  loading: boolean;
  error: string | null;
  serviceTypeSelect: {
    label: string;
    value: number;
  }[];
  offset: number;
  limit: number;
  sortField: string;
  sortOrder: number;
  dataCount: number;
  searchInput: string;
  selectedRequest: IRequestRes | null;
}

const initialState: RequestState = {
    requests: [],
    loading: false,
    error: null,
    dataCount: 0,
    offset: 0,
    limit: 10,
    sortField: "createdOn",
    sortOrder: 1,
    searchInput: "",
    serviceTypeSelect: [],
    selectedRequest: null
};

export const getAllRequest = createAsyncThunk<IRequestResponse, ICommonListPayloadDto>(
  'requests/fetch',
  async (payload, { rejectWithValue }) => {
    try {
        const response = await Axios.get<IRequestResponse>(`requests`, { params: payload });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch services");
    }
  }
);

export const getRequestById = createAsyncThunk<IRequestResponse, number>(
  'requests/fetchById',
  async (id, { rejectWithValue }) => {
    const response = await Axios.get<IRequestResponse>(`requests/${id}`);
    return response.data;
  }
);


export const addRequest = createAsyncThunk<
  IRequestResponse,
  IRequestReq,
  { rejectValue: string }
>("request/add", async (values, { rejectWithValue }) => {
  try {
    const response = await Axios.post<IRequestResponse>(
      "requests",
      values
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to add request"
    );
  }
});

//  update service
export const updateRequest = createAsyncThunk<
  IRequestResponse,
  {
    id: number;
    customer_id: number;
    vendor_id: number;
    service_id: number;
    sub_service_id: number;
  },
  { rejectValue: string }
>("request/update", async (value, { rejectWithValue }) => {
  try {
    const response = await Axios.put<IRequestResponse>(`requests/${value.id}`, {
      customer_id: value.customer_id,
      vendor_id: value.vendor_id,
      service_id: value.service_id,
      sub_service_id: value.sub_service_id
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to update request");
  }
});

// delete service
export const deleteRequest = createAsyncThunk<
  number, // Return type (deleted ID)
  number, // Input type (ID to delete)
  { rejectValue: string }
>("request/delete", async (id, { rejectWithValue }) => {
  try {
    await Axios.delete(`requests/${id}`);
    return id;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete request"
    );
  }
});



// Create Slice
const RequestSlice = createSlice({
  name: "request",
  initialState,
  reducers: {
    setOffset: (state, action) => {
      state.offset = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSortField: (state, action) => {
      state.sortField = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setSearchInput: (state, action) => {
      state.searchInput = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllRequest.fulfilled, (state, action: PayloadAction<IRequestResponse>) => {
        state.loading = false;
        if (Array.isArray(action.payload.data.data)) {
          state.requests = action.payload.data.data;
          state.dataCount = action.payload.data.total;
        } else {
          state.requests = [];
          state.dataCount = 0;
        }
      })

      .addCase(getAllRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to load requests";
      })

      .addCase(getRequestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRequestById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload.data && Array.isArray(action.payload.data)) {
          state.selectedRequest = action.payload.data[0];
        }
      })
      .addCase(addRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(addRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload.data) {
          state.requests = [...state.requests, action.payload.data[0]];
        }
      })

      .addCase(addRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to add request";
      })

      .addCase(updateRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRequest.fulfilled, (state, action) => {
        if (!action.payload?.data) {
          console.error("Update payload missing data:", action.payload);
          return;
        }

        state.loading = false;
        state.error = null;
      })

      .addCase(updateRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update request";
      })

      .addCase(deleteRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRequest.fulfilled, (state, action) => {
        if (typeof action.payload !== "number") {
          console.error("Delete failed, invalid payload:", action.payload);
          return;
        }

        state.requests = state.requests.filter(
          (cat) => cat.id !== action.payload
        );
        state.loading = false;
      })

      .addCase(deleteRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete request";
      })
  },
});

export const { setOffset, setLimit, setSortField, setSortOrder, setSearchInput } = RequestSlice.actions;
export default RequestSlice.reducer;
